import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { AdminOverlaysProvider } from './overlays';
import { Button, Modal, Spinner } from './ui';
import SiteCredit from '../SiteCredit';
import api from '../../utils/api';
import { checkSession, clearSession, getUser, updateActivity } from '../../utils/session';

const AdminUserContext = createContext(null);
export const useAdminUser = () => useContext(AdminUserContext);

const COLLAPSE_KEY = 'pb-admin-sidebar-collapsed';

/** This panel's own session only — the merchant portal keeps its own. */
const endAdminSession = () => clearSession('admin');

/**
 * Shell for every /admin/* route: session guard, sidebar, topbar and content.
 *
 * The guard lives here rather than in each page, so a new admin page is
 * protected by being placed inside this route.
 */
const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');

  const performLogout = useCallback(() => {
    endAdminSession();
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  /**
   * Signing out is always confirmed. Guarding here rather than on each button
   * covers every route out of the session — the sidebar icon and the topbar
   * menu — from one place. Session expiry still signs out silently, because
   * that is not a choice the user is making.
   */
  const requestLogout = useCallback(() => setLogoutOpen(true), []);

  // Session guard: valid local session, then confirm the token with the server.
  useEffect(() => {
    if (!checkSession('admin')) {
      endAdminSession();
      navigate('/admin/login', { replace: true });
      return;
    }

    const stored = getUser('admin');
    // A valid token is not by itself permission to be here — a merchant token
    // is perfectly authentic and must never render the admin console.
    if (stored?.role !== 'admin') {
      endAdminSession();
      navigate('/admin/login', { replace: true });
      return;
    }
    setUser(stored);

    updateActivity('admin');

    let cancelled = false;
    api
      .get('/api/auth/verify')
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.user?.role !== 'admin') {
          endAdminSession();
          navigate('/admin/login', { replace: true });
          return;
        }
        setVerifying(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Only a genuine auth rejection ends the session. A 429 from the rate
        // limiter, a 5xx, or a momentary network gap (a server restart, a
        // flaky connection) is not evidence that the token is invalid — and
        // treating it as such logged people out mid-session.
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          endAdminSession();
          navigate('/admin/login', { replace: true });
          return;
        }
        setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // A token that expires mid-session must land the admin back on the login
  // screen. The interceptor is scoped to this layout (and ejected on unmount)
  // so public pages sharing the api module are unaffected.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          endAdminSession();
          navigate('/admin/login', { replace: true });
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, [navigate]);

  // Keep the session alive while the admin is actually working.
  useEffect(() => {
    const onActivity = () => updateActivity('admin');
    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);
    return () => {
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      localStorage.setItem(COLLAPSE_KEY, current ? '0' : '1');
      return !current;
    });
  };

  if (verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div className="flex items-center gap-3 text-sm font-medium text-ink-500">
          <Spinner className="h-5 w-5 text-brand-700" />
          Loading admin…
        </div>
      </div>
    );
  }

  return (
    <AdminUserContext.Provider value={user}>
      <AdminOverlaysProvider>
        <div className="min-h-screen bg-ink-50">
          <Sidebar
            collapsed={collapsed}
            mobileOpen={mobileNavOpen}
            onCloseMobile={() => setMobileNavOpen(false)}
            onToggleCollapse={toggleCollapsed}
          />

          <div className={`flex min-h-screen flex-col transition-[padding] duration-200 ${collapsed ? 'lg:pl-[76px]' : 'lg:pl-64'}`}>
            <Topbar
              pathname={location.pathname}
              user={user}
              onOpenMobileNav={() => setMobileNavOpen(true)}
              onLogout={requestLogout}
            />
            <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
              <Outlet />
            </main>

            <footer className="border-t border-ink-200 bg-white px-4 py-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-ink-500">
                  ParcelBuddy admin · © {new Date().getFullYear()}
                </p>
                <SiteCredit />
              </div>
            </footer>
          </div>
        </div>

        <Modal
          open={logoutOpen}
          onClose={() => setLogoutOpen(false)}
          title="Sign out?"
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setLogoutOpen(false)}>
                Stay signed in
              </Button>
              <Button variant="danger" onClick={performLogout}>
                Sign out
              </Button>
            </>
          }
        >
          <p className="text-sm leading-relaxed text-ink-600">
            You&apos;ll need to sign in again to get back into the admin console. Any unsaved
            changes on this page will be lost.
          </p>
        </Modal>
      </AdminOverlaysProvider>
    </AdminUserContext.Provider>
  );
};

export default AdminLayout;
